import Head from "next/head";
import Script from "next/script";
import Image from "next/image";
import RFButton from "@/components/RFButton";
import React, { ReactElement, useEffect, useState } from "react";
import styles from "./index.module.scss";
import * as LoginServices from "@/service/login.services";
import { RegionModel, RegisterParams } from "@/interfaces/loginTypes";
import { GetStaticProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import rfStore from "@/utils/rfStore";
import useWindowSize from "@/hooks/useWindowSize";
import * as Validation from "@/utils/validate";
import cn from "classnames";
import IconFont from "@/components/IconFont";
import RFLayout from "@/layout/RFLayout";
import Toast from "@/components/Toast";
import { setAuthInfo } from "@/utils/storage";

enum UIState {
	PHONE = "PHONE",
	EMAIL = "EMAIL",
}

// 中文默认
const ChinaMainland = {
	acronym: "CN",
	currency: "",
	currencyName: "",
	currencySymbol: "",
	emoji: "",
	englishName: "",
	name: "China Mainland",
	phoneCode: "86",
};

// 英语 默认
const ArabEmirates = {
	acronym: "AE",
	currency: "",
	currencyName: "",
	currencySymbol: "",
	emoji: "",
	englishName: "",
	name: "United Arab Emirates",
	phoneCode: "971",
};

const COUNT_TOTAL = 60;

export default function Login() {
	const { t } = useTranslation("login");
	const router = useRouter();
	const [uiStatus, setUiStatus] = useState(UIState.PHONE);
	const [loading, setLoading] = useState(false);
	const [selectedRegion, setSelectedRegion] = useState<RegionModel>(ChinaMainland);
	const [regions, setRegions] = useState<RegionModel[]>([]);
	const [sendCodeDisabled, setSendCodeDisabled] = useState(false);
	const [count, setCount] = useState(COUNT_TOTAL);
	const [widgetId, setWidgetId] = useState(-1);

	const [formData, setFormData] = useState({
		phone: "",
		email: "",
		validationCode: "",
	});
	const [errors, setErrors] = useState({
		phone: "",
		email: "",
		validationCode: "",
	});
	const [activeField, setActiveField] = useState("");

	const [selectedAreaVisible, setSelectedAreaVisible] = useState(false);
	const { height, width } = useWindowSize();

	useEffect(() => {
		const rfState = rfStore.state;
		if (rfState) {
			rfState["region"] = selectedRegion.acronym;
			rfStore.setRFState(rfState);
		}
	}, [selectedRegion]);

	useEffect(() => {
		const language = router.locale || "en";
		LoginServices.getRegions(language).then((regions) => {
			setRegions(regions);
		});
	}, [router.locale]);

	useEffect(() => {
		const visibleLister = () => {
			if (selectedAreaVisible) {
				setSelectedAreaVisible(false);
			}
		};
		document.addEventListener("click", visibleLister, false);
		document.addEventListener("touchStart", visibleLister, false);

		return () => {
			document.removeEventListener("click", visibleLister);
			document.removeEventListener("touchStart", visibleLister);
		};
	}, [selectedAreaVisible]);
	// 倒计时处理
	useEffect(() => {
		if (!sendCodeDisabled) {
			return;
		}
		let timer = setInterval(() => {
			if (count === 0) {
				clearTimeout(timer);
				setSendCodeDisabled(false);
				setCount(COUNT_TOTAL);
				return;
			}
			setCount(count - 1);
		}, 1000);

		return () => {
			if (timer) {
				clearTimeout(timer);
			}
		};
	}, [count, sendCodeDisabled]);

	// 验证码
	const handleSendCode = () => {
		if (sendCodeDisabled) {
			return;
		}
		const verified = uiStatus == UIState.EMAIL ? validateForm("email") : validateForm("phone");

		if (!verified) {
			return;
		}

		if (widgetId > -1) {
			window.grecaptcha.enterprise.reset(widgetId);
			return;
		}
		renderCaptchaWidget();
	};

	const renderCaptchaWidget = () => {
		const SITE_KEY = process.env.NEXT_PUBLIC_CAPTCHA_KEY;
		const opt_widget_id = window.grecaptcha.enterprise.render("g_recaptcha", {
			sitekey: SITE_KEY,
			theme: "light",
			callback(securityToken: string) {
				sendVerifiedCode(securityToken);
			},
		});
		setWidgetId(opt_widget_id);
	};

	const sendVerifiedCode = async (securityToken: string) => {
		setSendCodeDisabled(true);

		const type = uiStatus === UIState.PHONE ? 1 : 2;
		const identity = uiStatus === UIState.PHONE ? getFullPhoneNumber() : formData.email;

		try {
			await LoginServices.sendVerifiedCode(type, identity, securityToken);
			Toast.success(t("send-success"));
		} catch (error) {
			let message;
			if (error instanceof Error) message = error.message;
			else message = String(error);
			Toast.fail(message);
		}
	};

	// 登录
	const handleLogin = async () => {
		const verified = checkSubmitData();
		if (!verified) {
			return;
		}

		const type = uiStatus === UIState.PHONE ? 1 : 2;
		const identity = uiStatus === UIState.PHONE ? getFullPhoneNumber() : formData.email;

		setLoading(true);
		try {
			const resData = await LoginServices.validateCodeIsSafety(type, identity, formData.validationCode);

			if (!resData) return;
			const { accessToken } = resData;
			const isLogin = await LoginServices.hasRegister(identity, type);
			let res = { token: "", userId: "" };

			if (isLogin) {
				res = (await LoginServices.login({
					type: 3,
					identity,
					accessToken,
				})) as any;
			} else {
				const registerParams: RegisterParams = {
					type: 1,
					accessToken,
					region: selectedRegion.acronym,
				};
				if (router.query.inviteCode) {
					registerParams.activity = {
						invitationCode: router.query.inviteCode as string,
					};
				}
				res = (await LoginServices.register(registerParams)) as any;
			}

			const { token, userId } = res;
			const _rfStore = rfStore.getRFState();
			if (_rfStore) {
				rfStore.setRFState({
					..._rfStore,
					userId,
					token,
				});
			}

			setAuthInfo(userId, token, selectedRegion.acronym);

			const redirectUrl = router.query.redirect as string;

			router.replace(redirectUrl ? decodeURIComponent(redirectUrl) : "/");
		} catch (error) {
			let message;
			if (error instanceof Error) message = error.message;
			else message = String(error);
			Toast.fail(message);
		} finally {
			setLoading(false);
		}
	};

	const getFullPhoneNumber = () => {
		const phoneNumber = String(selectedRegion.phoneCode).padStart(4, "0") + formData.phone.replaceAll(" ", "");
		return phoneNumber.replace(/-/g, "").replace(/\+/g, "");
	};

	const validateForm = (field: string) => {
		if (field === "phone" && !Validation.validatePhone(selectedRegion.acronym, formData.phone)) {
			setErrors({ ...errors, [field]: t("validatePhoneMsg") });
			return false;
		}

		if (field === "email" && !Validation.validateEmail(formData.email)) {
			setErrors({ ...errors, [field]: t("validateEmailMsg") });
			return false;
		}

		if (field === "validationCode" && !Validation.validateValidateCode(formData.validationCode)) {
			setErrors({ ...errors, [field]: t("validateCodeMsg") });
			return false;
		}
		return true;
	};

	const checkSubmitData = () => {
		const verifiedCode = validateForm("validationCode");
		if (uiStatus == UIState.EMAIL) {
			return validateForm("email") && verifiedCode;
		}
		if (uiStatus == UIState.PHONE) {
			return validateForm("phone") && verifiedCode;
		}
		return false;
	};

	const handleFocus = (e: React.FocusEvent, field: string) => {
		setActiveField(field);
	};

	const handleBlur = (e: React.FocusEvent, field: string) => {
		setActiveField("");
		validateForm(field);
	};

	const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
		const nextFormData = {
			...formData,
			[e.currentTarget.name]: e.currentTarget.value,
		};
		const nextErrors = {
			...errors,
			[e.currentTarget.name]: "",
		};

		setFormData(nextFormData);
		setErrors(nextErrors);
	};

	const renderSelectedControl = () => {
		return (
			<div className={styles.selected} onClick={() => setSelectedAreaVisible(true)}>
				<span>+{selectedRegion.phoneCode}</span>

				{selectedAreaVisible ? (
					<IconFont className={styles.arrow} type="ArrowUp" />
				) : (
					<IconFont className={styles.arrow} type="ArrowDown" />
				)}
			</div>
		);
	};

	const renderSelectedArea = (
		<ul className={styles.selectedArea}>
			{regions.map((regionModel, index) => {
				return (
					<li key={regionModel.phoneCode + index} onClick={() => setSelectedRegion(regionModel)}>
						<span>+{regionModel.phoneCode}</span>
						<span>{regionModel.name}</span>
					</li>
				);
			})}
		</ul>
	);

	const sendCodeText = () => {
		if (!sendCodeDisabled) {
			return t("buttonText");
		}

		const timeText = count === 0 ? "" : count < 10 ? "0" + count : count;
		const text = t("resetSendText");
		return `${text as string}(${String(timeText)})`;
	};

	return (
		<>
			<Head>
				<title>Login</title>
			</Head>

			<Script src="https://www.recaptcha.net/recaptcha/enterprise.js"></Script>

			<div className={styles.outContainer} style={{ minHeight: height }}>
				<div className={styles.container} style={{ minHeight: width > 768 ? height * 0.75 : height }}>
					<header className={styles.header}>
						<div className={styles.logo}>
							<Image
								src="https://web-cdn.rockflow.tech/h5/earning/logo.png"
								width={168}
								height={54}
								alt="logo"
								quality="100"
								priority={true}
								style={{
									maxWidth: "100%",
									height: "auto",
								}}
							></Image>
						</div>
					</header>
					<div className={styles.form}>
						<div className={styles.top}>
							<h2>
								{t("title1")} <br /> {t("title2")}
							</h2>

							{uiStatus === UIState.PHONE && (
								<div className={styles.formItem}>
									<div className={cn(styles.formFiled, activeField === "phone" ? styles.active : "")}>
										{renderSelectedControl()}
										<input
											type="number"
											name="phone"
											placeholder={t("placeholderTextWithPhone")}
											value={formData.phone}
											onFocus={(e) => handleFocus(e, "phone")}
											onBlur={(e) => handleBlur(e, "phone")}
											onChange={handleChange}
										/>
										{selectedAreaVisible && renderSelectedArea}
									</div>

									{errors.phone && <div className={styles.formErrorMsg}>{errors.phone}</div>}
								</div>
							)}

							{uiStatus === UIState.EMAIL && (
								<div className={styles.formItem}>
									<div className={cn(styles.formFiled, activeField === "email" ? styles.active : "")}>
										<input
											type="email"
											name="email"
											placeholder={t("placeholderTextWithEmail")}
											value={formData.email}
											onFocus={(e) => handleFocus(e, "email")}
											onBlur={(e) => handleBlur(e, "email")}
											onChange={handleChange}
										/>
									</div>
									{errors.email && <div className={styles.formErrorMsg}>{errors.email}</div>}
								</div>
							)}

							<div className={styles.formItem}>
								<div className={cn(styles.sendGroup, styles.sendGroupSmall)}>
									<div className={cn(styles.formFiled, activeField === "validationCode" ? styles.active : "")}>
										<input
											type="number"
											name="validationCode"
											maxLength={6}
											placeholder={t("placeholderTextWithCode")}
											value={formData.validationCode}
											onFocus={(e) => handleFocus(e, "validationCode")}
											onBlur={(e) => handleBlur(e, "validationCode")}
											onChange={handleChange}
										/>
										<span className={styles.sendSpan} onClick={handleSendCode}>
											{sendCodeText()}
										</span>
									</div>
									<span className={styles.sendLarge} onClick={handleSendCode}>
										{sendCodeText()}
									</span>
								</div>
								{errors.validationCode && <div className={styles.formErrorMsg}>{errors.validationCode}</div>}
							</div>

							<div className={styles.captcha} id="g_recaptcha"></div>
						</div>

						<div className={styles.bottom}>
							<p
								onClick={() => {
									if (uiStatus === UIState.EMAIL) {
										setUiStatus(UIState.PHONE);
									} else {
										setUiStatus(UIState.EMAIL);
									}
								}}
							>
								{uiStatus === UIState.PHONE ? t("useEmailLogin") : t("usePhoneLogin")}
							</p>
							<RFButton type="primary" loading={loading} wrapperClass={styles.control} onClick={handleLogin}>
								{t("loginBtnText")}
							</RFButton>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}

export const getStaticProps: GetStaticProps = async ({ locale = "en" }) => ({
	props: {
		...(await serverSideTranslations(locale, ["login"])),
	},
});

Login.getLayout = function getLayout(page: ReactElement) {
	return <RFLayout>{page}</RFLayout>;
};
